import type {
  AdminStats,
  OrganizationListResponse,
  OrganizationDetailResponse,
  SetOverrideRequest,
  SetOverrideResponse,
  RemoveOverrideResponse,
  ErrorResponse,
} from '../types/api';

export class APIError extends Error {
  statusCode: number;
  data?: unknown;

  constructor(message: string, statusCode: number, data?: unknown) {
    super(message);
    this.name = 'APIError';
    this.statusCode = statusCode;
    this.data = data;
  }
}

const getBaseURL = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return '/api';
};

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const baseURL = getBaseURL();
  const url = `${baseURL}${endpoint}`;

  const config: RequestInit = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    let data: unknown;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      const errorMessage =
        typeof data === 'object' && data !== null && 'error' in data
          ? (data as ErrorResponse).error
          : typeof data === 'string'
            ? data
            : `Request failed with status ${response.status}`;

      throw new APIError(errorMessage, response.status, data);
    }

    return data as T;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }

    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new APIError('Network error: Unable to connect to server', 0, error);
    }

    throw new APIError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      0,
      error
    );
  }
}

export interface ListOrganizationsParams {
  cursor?: string;
  limit?: number;
  sort?: 'name' | 'created' | 'members' | 'status';
  order?: 'asc' | 'desc';
  search?: string;
}

export interface AdminMeResponse {
  email: string;
  userId: string;
}

export interface WaitlistParams {
  sort?: 'email' | 'created_at';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface WaitlistResponse {
  signups: Array<{ id: string; email: string; created_at: string }>;
  total: number;
}

export const adminAPI = {
  getMe: (): Promise<AdminMeResponse> => fetchAPI<AdminMeResponse>('/admin/me'),

  getStats: (): Promise<AdminStats> => fetchAPI<AdminStats>('/admin/stats'),

  listOrganizations: (params: ListOrganizationsParams = {}): Promise<OrganizationListResponse> => {
    const searchParams = new URLSearchParams();
    if (params.cursor) searchParams.set('cursor', params.cursor);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.sort) searchParams.set('sort', params.sort);
    if (params.order) searchParams.set('order', params.order);
    if (params.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return fetchAPI<OrganizationListResponse>(`/admin/organizations${query ? `?${query}` : ''}`);
  },

  getOrganization: (id: string): Promise<OrganizationDetailResponse> =>
    fetchAPI<OrganizationDetailResponse>(`/admin/organizations/${id}`),

  setOverride: (id: string, data: SetOverrideRequest): Promise<SetOverrideResponse> =>
    fetchAPI<SetOverrideResponse>(`/admin/organizations/${id}/overrides`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  removeOverride: (id: string, confirm: boolean = false): Promise<RemoveOverrideResponse> =>
    fetchAPI<RemoveOverrideResponse>(
      `/admin/organizations/${id}/overrides${confirm ? '?confirm=true' : ''}`
      , { method: 'DELETE' }
    ),

  getWaitlist: (params: WaitlistParams = {}): Promise<WaitlistResponse> => {
    const searchParams = new URLSearchParams();
    if (params.sort) searchParams.set('sort', params.sort);
    if (params.order) searchParams.set('order', params.order);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.offset !== undefined) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString();
    return fetchAPI<WaitlistResponse>(`/admin/waitlist${query ? `?${query}` : ''}`);
  },
};
// Deploy: pick up corrected VITE_API_URL with /api prefix
